import {
  type CartItemResponse,
  type Locale,
  type ProductListItem,
  type ProductStockResponse,
} from '@aps/contracts';
import { pickText } from './format.js';

/**
 * Mijozga qoldiqni ko'rsatish.
 *
 * ENG MUHIM MATN QOIDASI (docs/02-MAHSULOT.md):
 * hech qachon "BOR" deyilmaydi — "QAYERDA bor" deyiladi.
 *
 *   O'z hududidagi do'konda   → "Urganch do'konida 3 ta"
 *   Faqat boshqa do'konda     → "Toshkent do'konida bor · 2–3 kunda keltiramiz"
 *   Hech qayerda yo'q         → "Hozircha tugagan"
 *   PRE_ORDER                 → "Buyurtma asosida · 18 kun"
 *
 * Ikkinchi qator muhim: tovarni YASHIRMASLIK kerak, muddat bilan
 * ko'rsatish kerak. Yashirilsa sotuv yo'qoladi, muddatsiz ko'rsatilsa
 * va'da buziladi.
 */

export type AvailabilityKind = 'here' | 'other' | 'out' | 'pre-order' | 'coming-soon';

export interface Availability {
  kind: AvailabilityKind;
  /** To'liq matn — mahsulot sahifasi uchun. */
  text: string;
  /** Qisqa matn — kartochka va ro'yxat uchun. */
  short: string;
  /** Darhol berish mumkinmi (checkout mantiqida ishlatiladi). */
  immediate: boolean;
}

export interface AvailabilityOptions {
  /** Mijoz hududidagi do'kon(lar). Bo'sh bo'lsa "o'z do'koni" aniqlanmaydi. */
  homeShopIds?: readonly string[];
  locale: Locale;
}

/** "Toshkent — Sergeli" → "Toshkent". Qatorga sig'ishi uchun. */
function shortShopName(row: ProductStockResponse, locale: Locale): string {
  const full = pickText(row.shopNameUz, row.shopNameRu, locale);
  const [head] = full.split(/\s+[—–-]\s+/);
  return (head ?? full).trim();
}

const TEXT = {
  UZ: {
    preOrder: (days: number) => `Buyurtma asosida · ${String(days)} kun`,
    preOrderShort: (days: number) => `${String(days)} kun`,
    here: (shop: string, qty: number) => `${shop} do‘konida ${String(qty)} ta`,
    hereShort: (qty: number) => `Do‘konda ${String(qty)} ta`,
    other: (shop: string) => `${shop} do‘konida bor · 2–3 kunda keltiramiz`,
    otherShort: '2–3 kunda',
    out: 'Hozircha tugagan',
    outShort: 'Tugagan',
    limited: (qty: number) => `Faqat ${String(qty)} ta bor`,
    comingSoon: 'Tez kunda',
    comingSoonNote: 'Tez kunda sotuvga chiqadi',
  },
  RU: {
    preOrder: (days: number) => `Под заказ · ${String(days)} дней`,
    preOrderShort: (days: number) => `${String(days)} дней`,
    here: (shop: string, qty: number) => `В магазине ${shop}: ${String(qty)} шт.`,
    hereShort: (qty: number) => `В магазине ${String(qty)} шт.`,
    other: (shop: string) => `Есть в магазине ${shop} · привезём за 2–3 дня`,
    otherShort: '2–3 дня',
    out: 'Сейчас закончился',
    outShort: 'Закончился',
    limited: (qty: number) => `Осталось только ${String(qty)} шт.`,
    comingSoon: 'Скоро',
    comingSoonNote: 'Скоро появится в продаже',
  },
} as const;

/** "Tez kunda" yorlig'i: narx o'rniga ko'rsatiladi. */
export function comingSoonLabel(locale: Locale): string {
  return TEXT[locale].comingSoon;
}

export function availability(
  item: Pick<ProductListItem, 'supplyType' | 'leadTimeDays' | 'stock'> & {
    comingSoon?: boolean;
  },
  options: AvailabilityOptions,
): Availability {
  const t = TEXT[options.locale];

  // "Tez kunda": narxsiz, hali sotuvda emas — qoldiq va muddat haqida gap yo'q.
  if (item.comingSoon === true) {
    return {
      kind: 'coming-soon',
      text: t.comingSoonNote,
      short: t.comingSoon,
      immediate: false,
    };
  }

  // PRE_ORDER da qoldiq umuman tekshirilmaydi — u ta'rifi bo'yicha
  // buyurtma asosida keltiriladi.
  if (item.supplyType === 'PRE_ORDER') {
    return {
      kind: 'pre-order',
      text: t.preOrder(item.leadTimeDays),
      short: t.preOrderShort(item.leadTimeDays),
      immediate: false,
    };
  }

  const home = new Set(options.homeShopIds ?? []);
  const withStock = item.stock.filter((row) => row.available > 0);

  const here = withStock.find((row) => home.has(row.shopId));
  if (here !== undefined) {
    return {
      kind: 'here',
      text: t.here(shortShopName(here, options.locale), here.available),
      short: t.hereShort(here.available),
      immediate: true,
    };
  }

  // Boshqa do'konda bor — YASHIRILMAYDI, muddat bilan ko'rsatiladi.
  const other = withStock[0];
  if (other !== undefined) {
    return {
      kind: 'other',
      text: t.other(shortShopName(other, options.locale)),
      short: t.otherShort,
      immediate: false,
    };
  }

  return { kind: 'out', text: t.out, short: t.outShort, immediate: false };
}

export interface CartLineNote {
  kind: 'pre-order' | 'out' | 'limited';
  text: string;
}

/**
 * Savat qatoridagi izoh.
 *
 * `PRE_ORDER` — muddat bilan (qoldiq yuritilmaydi, miqdor cheklanmaydi).
 * `IN_STOCK` da qoldiq yetmasa: butunlay tugagan yoki "Faqat N ta bor".
 * Yetarli bo'lsa izoh yo'q — qo'shimcha shovqin kerak emas.
 */
export function cartLineNote(
  item: Pick<CartItemResponse, 'supplyType' | 'leadTimeDays' | 'maxQty' | 'qty'>,
  locale: Locale,
): CartLineNote | null {
  const t = TEXT[locale];
  if (item.supplyType === 'PRE_ORDER') {
    return { kind: 'pre-order', text: t.preOrder(item.leadTimeDays) };
  }
  const max = item.maxQty ?? 0;
  if (max <= 0) return { kind: 'out', text: t.out };
  if (item.qty > max) return { kind: 'limited', text: t.limited(max) };
  return null;
}

/** Savatda buyurtmani to'sadigan qator bormi (IN_STOCK, qoldiq yetmaydi). */
export function cartBlocked(
  items: readonly Pick<CartItemResponse, 'supplyType' | 'maxQty' | 'qty'>[],
): boolean {
  return items.some((item) => item.supplyType === 'IN_STOCK' && item.qty > (item.maxQty ?? 0));
}

/**
 * Moslik matni.
 *
 * Hech qachon "MOS KELADI" deyilmaydi — bu kafolat bo'lib eshitiladi,
 * holbuki moslik `searchTags` orqali aniqlanadi va u EHTIMOLLIK
 * (docs/02-MAHSULOT.md → "Garaj").
 */
export function fitmentNote(locale: Locale): string {
  return locale === 'RU'
    ? 'Оператор проверит совместимость при подтверждении заказа.'
    : 'Buyurtmani operator tasdiqlashda moslikni tekshiradi.';
}

/**
 * Yetkazish narxi matni.
 *
 * Tizimda narx YO'Q va taxmin ham yozilmaydi
 * (docs/03-UI-UX.md → "Matn qoidalari").
 */
export function deliveryNote(locale: Locale): string {
  return locale === 'RU'
    ? 'Оператор позвонит и согласует доставку.'
    : 'Operator qo‘ng‘iroq qilib yetkazishni kelishadi.';
}

/** Yetkazish zonasi — hududga bog'langan xizmat va muddat. */
export interface DeliveryZoneLike {
  nameUz: string;
  nameRu: string | null;
  serviceUz: string;
  serviceRu: string | null;
  etaUz: string;
  etaRu: string | null;
  isFallback: boolean;
  regionIds: readonly string[];
}

/**
 * Hudud uchun zona.
 *
 * Hudud hech bir zonaga kirmasa ZAXIRA zona qaytadi — mijozga
 * "yetkazish yo'q" deyilmaydi, "operator kelishadi" deyiladi.
 * Zaxira zona BITTA bo'lishi kerak; ikkitasi bo'lsa qaysi biri
 * ishlatilishi tartibga bog'liq bo'lib qolardi (`avtopart-status-machine`
 * dagi bir xil mulohaza — tasodifiy natija xatodan yomonroq).
 */
export function zoneForRegion<T extends DeliveryZoneLike>(
  zones: readonly T[],
  regionId: string | null,
): T | null {
  if (regionId === null) return null;
  return (
    zones.find((zone) => zone.regionIds.includes(regionId)) ??
    zones.find((zone) => zone.isFallback) ??
    null
  );
}

/**
 * Zona haqidagi qator: "O'z kuryerimiz · 1 kun".
 *
 * NARX YO'Q va bu ataylab: u har lokatsiya uchun taksi bilan o'sha
 * zahoti gaplashiladi (`docs/06-OCHIQ-SAVOLLAR.md`, 7-javob). Tizimda
 * yetkazish narxi maydoni umuman yo'q, shuning uchun bu yerda ham
 * son ko'rsatilmaydi.
 */
export function zoneLine(zone: DeliveryZoneLike, locale: Locale): string {
  const service = locale === 'RU' ? (zone.serviceRu ?? zone.serviceUz) : zone.serviceUz;
  const eta = locale === 'RU' ? (zone.etaRu ?? zone.etaUz) : zone.etaUz;
  return `${service} · ${eta}`;
}
